import { cn } from "@norican/react";
import { useEffect, useMemo, useRef, useState } from "react";

import { NoricanLogo, NoricanMark } from "@/components/brand/norican-logo";
import { QrCode } from "@/components/qr-code";
import { useElementWidth } from "@/hooks/use-element-width";
import type { CardProfile } from "@/lib/api";
import { cardFace } from "@/lib/card-face";
import { chooseCardSymbol } from "@/lib/card-qr";

interface BusinessCardProps {
  card: CardProfile;
  /** Start on the back (Norican Blue) side. */
  initialSide?: "front" | "back";
  className?: string;
}

/*
 * The card follows the business-card template of the Norican Brand
 * Guidelines 3.0 (p.47): primary logo top-left, QR code top-right, name and
 * job title, e-mail and mobile, a thin rule, the company block bottom-left and
 * norican.com bottom-right. Everything on the front is Norican Blue on white
 * (p.21: text is never Accent Blue) through fixed brand tokens, so a theme
 * change cannot touch it; the back is Norican Blue with the white logo, one
 * electrified line (p.28), the icon mark in the top corner (p.15) and the
 * technology brand lock-up in the footer (Marketing's October 2026 update to
 * the template, as on the printed card).
 *
 * It is a screen card and the QR code is the element that has to scan from
 * another phone's camera, so the face has two compositions, switched on the
 * card's own width (container queries, `@min-[520px]`):
 *
 * LANDSCAPE, 520 px and wider: the 85 × 55 mm proportion of the template as a
 *   grid of two columns (text, QR) and three rows: the content row, a gap and
 *   the company row. The QR column is the template's "Norican Blue square",
 *   at least 28.5cqw wide and never narrower than 2.4 css px per module, so a
 *   longer vCard widens the column (and the card grows) instead of shrinking
 *   the modules. The text column of the content row is a flex column, logo /
 *   identity / contact / rule, as tall as the QR beside it and spaced with
 *   `justify-between`: the three gaps are equal ("plenty of evenly spaced
 *   room", p.24) and the rule ends exactly on the symbol's bottom edge, so the
 *   two read as one line instead of a near miss. The gap row below takes what
 *   the 85:55 face leaves, and the company block sits on the bottom margin as
 *   on p.47. Every length is a container-query unit; the 85:55 ratio is the
 *   face's minimum height (overflow-clip is not a scroll container, so the
 *   automatic minimum size applies), so a card whose Entra data does not fit
 *   grows taller instead of clipping or truncating.
 *
 * PORTRAIT, below 520 px (every phone): the same elements stacked on one
 *   left margin: logo, identity, contact, the QR centred at 78 % of the
 *   inner width, rule, company block with norican.com on its right. On a 390 px
 *   phone the symbol is 247 px = 4.3 css px per module (13 device px at DPR 3);
 *   type is fixed at the design-system sizes (name 24 px, body 14 px, company
 *   12 px) rather than a fraction of the width, so nothing drops under the
 *   legible floor. The text-column wrapper is `display: contents` here, so
 *   its children take part in the face's grid and the `order` utilities put
 *   the QR between the contact block and the rule.
 *
 * The inlined logo SVG carries its exclusion zone (21.56 of 345.78 units on
 * every side = 6.235 %), so a negative margin of that amount puts the art, not
 * the padding, on the card margin; the zone below it keeps the name clear of
 * the logo. The art is 64.1 % of the viewBox height: a 55 px box is 35 px of
 * art on phones, a 13cqw box is 8.3cqw (47 px on a 560 px card), both at or
 * above the 35 px digital minimum of p.17. The logo keeps its intrinsic width
 * (`self-start`, `justify-self-start`), so the "N" lands exactly on the left
 * margin instead of being centred in a stretched box.
 *
 * The 3D flip: `transform-3d` and `backface-hidden` are set only while the
 * card turns. A static face is then an ordinary, unpromoted box that the
 * browser rasterises on the device-pixel grid; as a composited 3D layer it
 * would be resampled at fractional device pixel ratios (Windows 125 %,
 * browser zoom), which softens the QR modules. The face turned away is hidden
 * with `visibility` from the 90° point of the turn, so no back face is needed
 * once the turn has ended.
 */

const TEXT_BODY = "text-sm leading-[1.2] @min-[520px]:text-[length:2.9cqw]";
const TEXT_COMPANY = "text-xs leading-[1.2] @min-[520px]:text-[length:2.6cqw]";

/* The numbers the QR edge is computed from; they mirror the classes on the front face. */
const LANDSCAPE_MIN_WIDTH = 520;
const QR_COLUMN_FRACTION = 0.285;
const INNER_WIDTH_FRACTION = 1 - 0.058 - 0.048; // card width minus the left and right margins
const PORTRAIT_QR_FRACTION = 0.78; // the same reduction Marketing asked for on the landscape face
const MIN_MODULE_PX = 2.4;

/** Length of the flip, mirrors `duration-700` on the turning element. */
const FLIP_MS = 700;

export function BusinessCard({ card, initialSide = "front", className }: BusinessCardProps) {
  const [flipped, setFlipped] = useState(initialSide === "back");
  const [turning, setTurning] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(containerRef);
  const { name, jobTitle, company, email, phone, street, cityLine, country, website } =
    cardFace(card);
  const [emailLocal, emailDomain] = email;
  const symbol = useMemo(() => chooseCardSymbol(card.vcard), [card.vcard]);
  // The symbol's edge comes from the card width, not from its own box: the
  // QR column is sized by the symbol (minmax(28.5cqw, auto)), so measuring the
  // box would be circular.
  const qrEdge =
    width === null
      ? undefined
      : width >= LANDSCAPE_MIN_WIDTH
        ? Math.max(width * QR_COLUMN_FRACTION, symbol.matrix.size * MIN_MODULE_PX)
        : width * INNER_WIDTH_FRACTION * PORTRAIT_QR_FRACTION;

  // The 3D context lives only for the length of the turn (a timer rather than
  // transitionend, which never fires when animations are disabled).
  useEffect(() => {
    if (!turning) {
      return;
    }
    const timer = window.setTimeout(() => setTurning(false), FLIP_MS + 50);
    return () => window.clearTimeout(timer);
  }, [turning, flipped]);

  const flip = () => {
    setFlipped((value) => !value);
    setTurning(true);
  };

  return (
    <div ref={containerRef} className={cn("@container w-full", className)}>
      <button
        type="button"
        onClick={flip}
        aria-pressed={flipped}
        aria-label={flipped ? "Show the front of the card" : "Show the back of the card"}
        className="focus-visible:ring-accent block w-full cursor-pointer rounded-md perspective-distant focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
      >
        <div
          className={cn(
            "relative transition-transform duration-700 ease-in-out",
            turning && "transform-3d",
            flipped && "rotate-y-180",
          )}
        >
          {/* Front. The face turned away is also hidden at the 90° point (350 ms of the 700 ms flip), for engines that do not cull back faces. */}
          <div
            className={cn(
              "text-brand-blue-700 relative grid grid-cols-[minmax(0,1fr)_auto] overflow-clip rounded-md bg-white pt-[4.6cqw] pr-[4.8cqw] pb-[4.6cqw] pl-[5.8cqw] text-left shadow-lg transition-[visibility] delay-[350ms] duration-0",
              "@min-[520px]:aspect-[85/55] @min-[520px]:grid-cols-[minmax(0,1fr)_minmax(28.5cqw,auto)] @min-[520px]:grid-rows-[auto_minmax(2.4cqw,1fr)_auto] @min-[520px]:gap-x-[2.8cqw]",
              turning && "backface-hidden",
              flipped && "invisible",
            )}
          >
            {/* Text column: a flex column as tall as the QR in landscape, `contents` in portrait */}
            <div className="contents @min-[520px]:col-start-1 @min-[520px]:row-start-1 @min-[520px]:flex @min-[520px]:min-w-0 @min-[520px]:flex-col @min-[520px]:justify-between @min-[520px]:gap-y-[2.4cqw]">
              <NoricanLogo
                variant="primary"
                className="col-span-2 -mt-[9.9px] -ml-[9.9px] h-[55px] w-auto self-start justify-self-start @min-[520px]:-mt-[2.34cqw] @min-[520px]:-ml-[2.34cqw] @min-[520px]:h-[13cqw]"
              />

              <div className="col-span-2 mt-5 min-w-0 @min-[520px]:mt-0">
                <p className="font-display leading-display text-xl @min-[520px]:text-[length:5.4cqw]">
                  {name}
                </p>
                {jobTitle && (
                  <p className={cn("mt-1 @min-[520px]:mt-[0.8cqw]", TEXT_BODY)}>{jobTitle}</p>
                )}
              </div>

              <div className={cn("col-span-2 mt-3 min-w-0 @min-[520px]:mt-0", TEXT_BODY)}>
                {emailLocal && (
                  <p className="wrap-anywhere">
                    {emailLocal}
                    {emailDomain && (
                      <>
                        <wbr />@{emailDomain}
                      </>
                    )}
                  </p>
                )}
                {phone && (
                  <p className="tabular-nums">
                    {phone.prefix} {phone.value}
                  </p>
                )}
              </div>

              {/* The rule: last in the column, so its line ends on the QR's bottom edge in landscape */}
              <div
                className="bg-brand-blue-700 order-2 col-span-2 mt-5 h-px w-full shrink-0 @min-[520px]:mt-0"
                aria-hidden="true"
              />
            </div>

            {/* The QR code is the template's Norican Blue square: dark modules straight on the white card, the card margin and the column gap as quiet zone */}
            <QrCode
              value={symbol.value}
              matrix={symbol.matrix}
              quietZone={0}
              edge={qrEdge}
              minModulePx={MIN_MODULE_PX}
              label={`QR code with the contact details of ${name}`}
              className="order-1 col-span-2 mt-5 @min-[520px]:col-span-1 @min-[520px]:col-start-2 @min-[520px]:row-start-1 @min-[520px]:mt-0 @min-[520px]:self-start"
              symbolClassName="mx-auto @min-[520px]:mr-0 @min-[520px]:ml-auto"
            />

            <div
              className={cn(
                "order-3 col-start-1 mt-3 min-w-0 self-baseline-last @min-[520px]:row-start-3 @min-[520px]:mt-0",
                TEXT_COMPANY,
              )}
            >
              <p className="font-bold">{company}</p>
              {street && <p className="wrap-anywhere whitespace-pre-line">{street}</p>}
              {cityLine && <p>{cityLine}</p>}
              {country && <p>{country}</p>}
            </div>

            <p
              className={cn(
                "order-3 col-start-2 mt-3 self-baseline-last font-bold @min-[520px]:row-start-3 @min-[520px]:mt-0 @min-[520px]:justify-self-end",
                TEXT_BODY,
              )}
            >
              {website}
            </p>
          </div>

          {/* Back: Norican Blue, the white logo, one electrified line, the icon mark in the top
              corner and the technology brand lock-up in the footer. The logo and the line form
              one group on the vertical centre of the room above the footer, whatever the face's
              height (the portrait face is as tall as the QR makes the front), and the line runs
              directly under the logo so it anchors it (p.28: connect it to a key part). */}
          <div
            className={cn(
              "bg-brand-blue-700 absolute inset-0 flex rotate-y-180 flex-col items-center justify-center overflow-hidden rounded-md pb-[8cqw] text-white shadow-lg transition-[visibility] delay-[350ms] duration-0",
              turning && "backface-hidden",
              !flipped && "invisible",
            )}
          >
            <NoricanLogo variant="primary" className="h-[22cqw] w-auto" />
            {/* A straight line (p.28): a whole 2 css px tall at every card width, so it never renders as a blurred sub-pixel band */}
            <div
              className="bg-brand-blue-accent mt-[3cqw] h-[2px] w-full shrink-0 shadow-[0_0_2.5cqw_0.4cqw_var(--norican-color-brand-blue-accent)]"
              aria-hidden="true"
            />
            <NoricanMark className="text-brand-blue-accent absolute top-[5.8cqw] right-[5.8cqw] size-[5.6cqw]" />
            {/* The lock-up is Marketing's artwork (public/brand/lockup-white.png), never rebuilt from the single logos;
                only its brand row, the rule with "Norican" is cut because the big logo above already says it */}
            <img
              src="/brand/lockup-white.png"
              alt="Norican technology brands: DISA, Simpson, Monitizer, Wheelabrator, StrikoWestofen"
              width={2202}
              height={53}
              className="absolute inset-x-[5.8cqw] bottom-[5.8cqw] h-auto w-[88.4cqw] max-w-none"
            />
          </div>
        </div>
      </button>
    </div>
  );
}
